import {
  Menu,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
  type Omittable,
  type OnChangeFn,
  type SortingState,
  useIsMobile,
} from "@salestrak/ui";

import { FilterToggle } from "./filter-toggle";
import { FilterValueChip } from "./filter-value-chip";

/** One named ordering a Sort chip offers. The first preset is the server's
 *  own order, the EMPTY sort; the rest name a single column direction. */
export type SortPreset<TKey extends string> = {
  key: TKey;
  label: string;
  sorting: SortingState;
};

/**
 * The preset a sorting state reads back as: the empty sort is the first
 * preset, a single column direction matches the preset that names it, and
 * anything else (a multi-column sort, a header sort on a column no preset
 * names) is null.
 */
export function matchSortPreset<TKey extends string>(
  presets: readonly SortPreset<TKey>[],
  sorting: SortingState,
): TKey | null {
  const [first, ...rest] = sorting;
  if (rest.length > 0) return null;
  if (!first) return presets[0]?.key ?? null;
  return (
    presets.find(
      (p) => p.sorting[0]?.id === first.id && p.sorting[0]?.desc === first.desc,
    )?.key ?? null
  );
}

/**
 * A toolbar Sort chip over a list of presets. The column headers write the
 * same `sorting` state, so a header click that lands on a preset reads back
 * as that preset; one that does not is still a sort, and the chip says
 * "Custom" without claiming a preset it is not.
 *
 * On a phone the chip is its glyph alone, tinted while a sort is set: the
 * toolbar holds every chip at one footprint there so the row never wraps,
 * and the list the press opens names the order.
 */
export function SortPresetChip<TKey extends string>({
  presets,
  sorting,
  onSortingChange,
}: Omittable<{
  presets: readonly SortPreset<TKey>[];
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
}>) {
  const isMobile = useIsMobile();
  const matched = matchSortPreset(presets, sorting);
  const sorted = sorting.length > 0;
  const label = sorted
    ? (presets.find((p) => p.key === matched)?.label ?? "Custom")
    : undefined;

  return (
    <Menu>
      <MenuTrigger
        render={
          isMobile ? (
            <FilterToggle
              size="xs"
              icon="arrow-up-down"
              flag
              label="Sort"
              selected={sorted}
            />
          ) : (
            <FilterValueChip
              label="Sort"
              value={label}
              onClear={() => onSortingChange([])}
              clearLabel="Clear sort"
            />
          )
        }
      />
      <MenuPopup align="start" width="select">
        <MenuRadioGroup
          value={matched ?? ""}
          onValueChange={(value) => {
            const preset = presets.find((p) => p.key === value);
            if (preset) onSortingChange(preset.sorting);
          }}
        >
          {presets
            .map((preset) => (
              <MenuRadioItem key={preset.key} value={preset.key}>
                {preset.label}
              </MenuRadioItem>
            ))
            .flatMap((item, index) =>
              // The default order stands apart from the column orderings,
              // as "All" does from the values in the filter chips' lists.
              index === 1 ? [<MenuSeparator key="default-divider" />, item] : [item],
            )}
        </MenuRadioGroup>
      </MenuPopup>
    </Menu>
  );
}
